import { arrayRemove, arrayUnion, doc, type FieldValue, updateDoc } from 'firebase/firestore';
import { getFirestoreDb } from '../config';
import type { RoomMember, RoomRole } from '../../../types/room';
import { ROOMS_COLLECTION } from './constants';
import { getPersistentRoom } from './rooms';

/**
 * Adds a member to a room.
 */
export async function addRoomMember(
  roomId: string,
  email: string,
  userId: string,
  role: RoomRole = 'member'
): Promise<void> {
  const db = getFirestoreDb();
  const docRef = doc(db, ROOMS_COLLECTION, roomId);

  const member: RoomMember = {
    userId,
    email,
    role,
    joinedAt: new Date(),
  };

  const updates: { members: FieldValue; adminUserIds?: FieldValue } = {
    members: arrayUnion(member),
  };
  if (role === 'admin') {
    updates.adminUserIds = arrayUnion(userId, email.toLowerCase());
  }

  await updateDoc(docRef, updates);
}

/**
 * Removes a member from a room.
 */
export async function removeRoomMember(roomId: string, userId: string): Promise<void> {
  const db = getFirestoreDb();
  const room = await getPersistentRoom(roomId);

  if (!room) return;

  const memberToRemove = room.members.find((member) => member.userId === userId);
  if (!memberToRemove) return;

  const adminIdsToRemove = Array.from(
    new Set([userId, memberToRemove.email?.toLowerCase()].filter(Boolean) as string[])
  );

  const docRef = doc(db, ROOMS_COLLECTION, roomId);
  await updateDoc(docRef, {
    members: arrayRemove(memberToRemove),
    adminUserIds: arrayRemove(...adminIdsToRemove),
  });
}

/**
 * Updates a member's role in a room.
 */
export async function updateMemberRole(
  roomId: string,
  userId: string,
  newRole: RoomRole
): Promise<void> {
  const room = await getPersistentRoom(roomId);
  if (!room) return;

  const updatedMembers = room.members.map((member) =>
    member.userId === userId ? { ...member, role: newRole } : member
  );

  const adminUserIds = Array.from(
    new Set(
      updatedMembers
        .filter((m) => m.role === 'admin')
        .flatMap((m) => [m.userId, m.email?.toLowerCase()].filter(Boolean) as string[])
    )
  );

  const db = getFirestoreDb();
  const docRef = doc(db, ROOMS_COLLECTION, roomId);
  await updateDoc(docRef, { members: updatedMembers, adminUserIds });
}
